import { z } from 'zod';
import { moneyStringSchema, uuidSchema } from './common';

/**
 * A sale made on an offline till, exactly as it happened. The till has already
 * taken the money and handed over the goods, so the server records this rather
 * than re-pricing it (see `applyOfflineSale`).
 */
export const posOfflineSaleSchema = z.object({
  /** The sale's idempotency key: a retried sync can only ever land once. */
  clientSaleId: z.string().min(8).max(80),
  deviceId: z.string().min(4).max(64),
  /** The temporary number printed on the customer's receipt. */
  localNumber: z.string().min(1).max(40),
  soldAt: z.string().datetime(),
  soldByUserId: uuidSchema,
  branchId: uuidSchema,
  buyerUserId: uuidSchema,
  buyerSlotId: uuidSchema.nullable(),
  items: z
    .array(
      z.object({
        productId: uuidSchema,
        variantId: uuidSchema.optional(),
        qty: z.number().int().positive(),
        unitPrice: moneyStringSchema,
        unitPriceBase: moneyStringSchema,
      }),
    )
    .min(1)
    .max(200),
  payment: z.object({
    methodCode: z.string().min(1).max(64),
    methodLabel: z.string().min(1).max(80),
    // Never a wallet: an offline till can't see a balance.
    tenderKind: z.enum(['cash', 'exact']),
    amountTendered: moneyStringSchema,
  }),
  managerDiscount: z
    .object({
      pct: z.number().min(0).max(100).optional(),
      amount: z.number().min(0).optional(),
      reason: z.string().max(500).optional(),
    })
    .optional(),
  customFees: z
    .array(
      z.object({
        code: z.string().max(64).nullable(),
        label: z.string().min(1).max(80),
        amount: moneyStringSchema,
      }),
    )
    .max(50)
    .optional(),
  notes: z.string().max(1000).optional(),
  /** What the till charged, compared with what the server works out. */
  total: moneyStringSchema,
});
export type PosOfflineSale = z.infer<typeof posOfflineSaleSchema>;

/** A shift closed while offline. Expected cash is worked out at sync. */
export const posOfflineShiftCloseSchema = z.object({
  clientId: z.string().min(8).max(80),
  deviceId: z.string().min(4).max(64),
  closedByUserId: uuidSchema,
  branchId: uuidSchema,
  startAt: z.string().datetime(),
  closedAt: z.string().datetime(),
  actualCash: z.number().min(0),
  notes: z.string().max(1000).optional(),
});
export type PosOfflineShiftClose = z.infer<typeof posOfflineShiftCloseSchema>;

/** Why an applied offline sale needs a person to look at it. */
export const POS_OFFLINE_FLAGS = {
  stock_negative: 'Stock went below zero',
  total_mismatch: 'The till and the server worked out different totals',
  slot_not_active: "The buyer's slot was no longer active, so no commissions were paid",
  seller_not_verified: 'The cashier on the sale could not be verified',
  clock_ahead: "The till's clock was ahead, so the sale was dated when it synced",
} as const;
export type PosOfflineFlag = keyof typeof POS_OFFLINE_FLAGS;

/** One member price table: only what differs from the base price. */
export type PosOfflinePriceTable = {
  products: Record<string, { unitPrice: string; appliedDiscountPct: string }>;
  variants: Record<string, { unitPrice: string; appliedDiscountPct: string }>;
};

/** What a till downloads so it can price and find members with no internet. */
export type PosOfflineDirectory = {
  generatedAt: string;
  branchId: string;
  /** Deliberately no email or phone: the whole list sits on the device. */
  members: Array<{
    id: string;
    fullName: string | null;
    referralCode: string | null;
    /** Price table when no slot is selected; null means list price. */
    noSlotTable: string | null;
    slots: Array<{
      id: string;
      slotCode: string;
      membershipName: string;
      activatedAt: string | null;
      table: string | null;
    }>;
  }>;
  tables: Record<string, PosOfflinePriceTable>;
  /** The methods an offline till may take. */
  methods: Array<{
    code: string;
    label: string;
    tenderKind: 'cash' | 'exact';
    currency: 'cash';
    roleCodes: string[];
    fees: Array<{ code: string; label: string; calc: unknown }>;
  }>;
};
